import React from "react";
import { getSceneState } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { resolveScaleTargetFromSelection } from "../../utils/scaleSelection";
import { resolveVisualMeshTransformTargets } from "../../utils/visualMeshTransformTargets";
import { getSelectedVisualMeshIds } from "../../visual-mesh-multi-selection";
import { EMPTY_OBJECT } from "../scene/sceneConstants";
import { safeTraverse } from "../scene/sceneObjectLookups";

export function useGizmoTargetResolvers({ sceneRef }) {
  // Resolve currently selected transform-capable inspector target to its owning body UUID.
  // This lets gizmos work from mesh/geom/site/inertial selections, not only direct body selection.
  const resolveSelectedBodyUuidForGizmo = React.useCallback(() => {
    const selection = useSelectionStore.getState();
    const bodiesNow = getSceneState()?.bodies || EMPTY_OBJECT;
    const bodyValues = Object.values(bodiesNow || {});
    const hasBody = (uuid) => !!uuid && !!bodiesNow[uuid];

    if (hasBody(selection.selectedBody)) return selection.selectedBody;

    if (selection.selectedGeom) {
      const geomKey = String(selection.selectedGeom);
      const sep = geomKey.indexOf(":");
      if (sep > 0) {
        const hintedBodyUuid = geomKey.slice(0, sep);
        if (hasBody(hintedBodyUuid)) return hintedBodyUuid;
      }
      const geomUuid = sep > 0 ? geomKey.slice(sep + 1) : geomKey;
      for (const body of bodyValues) {
        if (!body || !Array.isArray(body.geoms)) continue;
        if (body.geoms.some((geom) => geom && String(geom.uuid) === String(geomUuid))) {
          return body.uuid;
        }
      }
    }

    if (selection.selectedSite) {
      const siteUuid = String(selection.selectedSite);
      for (const body of bodyValues) {
        if (!body || !Array.isArray(body.sites)) continue;
        if (body.sites.some((id) => String(id) === siteUuid)) return body.uuid;
      }
    }

    if (selection.selectedInertial) {
      const inertialKeyRaw = String(selection.selectedInertial);
      const sep = inertialKeyRaw.indexOf(":");
      if (sep > 0) {
        const hintedBodyUuid = inertialKeyRaw.slice(0, sep);
        if (hasBody(hintedBodyUuid)) return hintedBodyUuid;
      }
      const inertialKey = sep > 0 ? inertialKeyRaw.slice(sep + 1) : inertialKeyRaw;
      for (const body of bodyValues) {
        const inertia = body?.inertia;
        if (!inertia) continue;
        if (String(inertia.uuid) === inertialKey || String(inertia.name || "") === inertialKey) {
          return body.uuid;
        }
      }
    }

    if (selection.selectedMesh) {
      const meshUuid = String(selection.selectedMesh);
      for (const body of bodyValues) {
        if (!body || !Array.isArray(body.geoms)) continue;
        if (body.geoms.some((geom) => geom && String(geom.mesh || "") === meshUuid)) {
          return body.uuid;
        }
      }
    }

    return null;
  }, []);

  const findGeomSceneObjectForGizmo = React.useCallback(
    (bodyUuid, geomId, meshId = null) => {
      if (!sceneRef.current || !bodyUuid) return null;
      let found = null;
      const ascendToGeomTransformRoot = (obj) => {
        let cur = obj;
        let outer = null;
        while (cur) {
          if (cur.userData?.transformLayer === "outer") outer = cur;
          cur = cur.parent;
        }
        return outer || obj;
      };

      safeTraverse(sceneRef.current, (obj) => {
        if (found || !obj?.userData) return;
        const ud = obj.userData;
        if (ud.bodyUuid !== bodyUuid) return;
        if (geomId && ud.geomUuid && String(ud.geomUuid) === String(geomId)) {
          found = ascendToGeomTransformRoot(obj);
          return false;
        }
        if (meshId && ud.meshName && String(ud.meshName) === String(meshId)) {
          found = ascendToGeomTransformRoot(obj);
          return false;
        }
      });
      return found;
    },
    [sceneRef]
  );

  const findSiteSceneObjectForGizmo = React.useCallback(
    (siteUuid) => {
      if (!sceneRef.current || !siteUuid) return null;
      let found = null;
      safeTraverse(sceneRef.current, (obj) => {
        if (found || !obj?.userData || obj.userData.type !== "site") return;
        if (String(obj.userData.siteUuid || obj.userData.siteName || "") === String(siteUuid)) {
          found = obj;
          return false;
        }
      });
      return found;
    },
    [sceneRef]
  );

  const findInertialSceneObjectForGizmo = React.useCallback(
    (bodyUuid, inertialId) => {
      if (!sceneRef.current || !bodyUuid) return null;
      let found = null;
      safeTraverse(sceneRef.current, (obj) => {
        if (found || !obj?.userData || obj.userData.type !== "inertial") return;
        const ud = obj.userData;
        if (ud.bodyUuid !== bodyUuid) return;
        if (
          !inertialId ||
          String(ud.inertialUuid || "") === String(inertialId) ||
          String(ud.inertialName || "") === String(inertialId)
        ) {
          found = obj.isGroup ? obj : obj.parent;
          return false;
        }
      });
      return found;
    },
    [sceneRef]
  );

  const resolveSelectedTargetForPositionGizmo = React.useCallback(() => {
    const selection = useSelectionStore.getState();
    const sceneState = getSceneState();
    const bodiesNow = sceneState?.bodies || EMPTY_OBJECT;
    const jointsNow = sceneState?.joints || EMPTY_OBJECT;
    const bodyEntries = Object.entries(bodiesNow || {});
    const hasBody = (uuid) => !!uuid && !!bodiesNow[uuid];
    const selectedJointIdRaw = selection.selectedJoint ? String(selection.selectedJoint) : null;
    const selectedJointUuid = selectedJointIdRaw
      ? jointsNow[selectedJointIdRaw]
        ? selectedJointIdRaw
        : Object.values(jointsNow).find((joint) => String(joint?.name || "") === selectedJointIdRaw)
            ?.uuid || null
      : null;

    if (selection.selectedCamera) {
      const camDef = sceneState?.cameras?.[selection.selectedCamera];
      if (camDef) {
        return { kind: "camera", cameraUuid: selection.selectedCamera };
      }
    }

    if (selectedJointUuid && jointsNow[selectedJointUuid]) {
      for (const [bodyUuid, body] of bodyEntries) {
        if (!body || !Array.isArray(body.joints)) continue;
        const ownsJoint = body.joints.some((entry) => {
          if (typeof entry === "string") return entry === selectedJointUuid;
          return entry?.uuid === selectedJointUuid;
        });
        if (ownsJoint && hasBody(bodyUuid)) {
          return { kind: "joint", jointUuid: selectedJointUuid, bodyUuid };
        }
      }
    }

    if (getSelectedVisualMeshIds().size > 1) {
      return null;
    }

    if (selection.selectedGeom) {
      const geomKey = String(selection.selectedGeom);
      const sep = geomKey.indexOf(":");
      const hintedBodyUuid = sep > 0 ? geomKey.slice(0, sep) : null;
      const geomId = sep > 0 ? geomKey.slice(sep + 1) : geomKey;
      for (const [bodyUuid, body] of bodyEntries) {
        if (hintedBodyUuid && bodyUuid !== hintedBodyUuid) continue;
        if (!body || !Array.isArray(body.geoms)) continue;
        const geom = body.geoms.find((entry) => entry && String(entry.uuid) === String(geomId));
        if (geom && hasBody(bodyUuid)) {
          return {
            kind: "geom",
            bodyUuid,
            geomId: String(geom.uuid),
            object: findGeomSceneObjectForGizmo(bodyUuid, geom.uuid, geom.mesh || null),
          };
        }
      }
    }

    if (selection.selectedSite) {
      const siteUuid = String(selection.selectedSite);
      for (const [bodyUuid, body] of bodyEntries) {
        if (!body || !Array.isArray(body.sites)) continue;
        if (body.sites.some((id) => String(id) === siteUuid) && hasBody(bodyUuid)) {
          return {
            kind: "site",
            bodyUuid,
            siteUuid,
            object: findSiteSceneObjectForGizmo(siteUuid),
          };
        }
      }
      const worldSiteObj = findSiteSceneObjectForGizmo(siteUuid);
      if (worldSiteObj) {
        return {
          kind: "site",
          bodyUuid: "",
          siteUuid,
          object: worldSiteObj,
        };
      }
    }

    if (selection.selectedInertial) {
      const inertialKeyRaw = String(selection.selectedInertial);
      const sep = inertialKeyRaw.indexOf(":");
      const hintedBodyUuid = sep > 0 ? inertialKeyRaw.slice(0, sep) : null;
      const inertialId = sep > 0 ? inertialKeyRaw.slice(sep + 1) : inertialKeyRaw;
      for (const [bodyUuid, body] of bodyEntries) {
        if (hintedBodyUuid && bodyUuid !== hintedBodyUuid) continue;
        const inertia = body?.inertia;
        if (!inertia) continue;
        if (String(inertia.uuid) === inertialId || String(inertia.name || "") === inertialId) {
          return {
            kind: "inertial",
            bodyUuid,
            object: findInertialSceneObjectForGizmo(bodyUuid, inertialId),
          };
        }
      }
    }

    if (selection.selectedMesh) {
      const meshUuid = String(selection.selectedMesh);
      for (const [bodyUuid, body] of bodyEntries) {
        if (!body || !Array.isArray(body.geoms)) continue;
        const geom = body.geoms.find((entry) => entry && String(entry.mesh || "") === meshUuid);
        if (geom && hasBody(bodyUuid)) {
          return {
            kind: "geom",
            bodyUuid,
            geomId: String(geom.uuid),
            object: findGeomSceneObjectForGizmo(bodyUuid, geom.uuid, meshUuid),
          };
        }
      }
    }

    const fallbackBodyUuid = resolveSelectedBodyUuidForGizmo();
    if (fallbackBodyUuid && hasBody(fallbackBodyUuid)) {
      return { kind: "body", bodyUuid: fallbackBodyUuid };
    }
    return null;
  }, [
    resolveSelectedBodyUuidForGizmo,
    findGeomSceneObjectForGizmo,
    findSiteSceneObjectForGizmo,
    findInertialSceneObjectForGizmo,
  ]);

  const resolveSelectedTargetForRotationGizmo = React.useCallback(() => {
    const target = resolveSelectedTargetForPositionGizmo();
    return target?.kind === "joint" ? null : target;
  }, [resolveSelectedTargetForPositionGizmo]);

  const resolveSelectedVisualMeshGeomTargetsForGizmo = React.useCallback(() => {
    const result = resolveVisualMeshTransformTargets(
      getSceneState()?.bodies || EMPTY_OBJECT,
      getSelectedVisualMeshIds()
    );
    if (!result.canTransform) return [];
    return result.targets.map((target) => ({
      ...target,
      object: findGeomSceneObjectForGizmo(target.bodyUuid, target.geomId, target.meshUuid),
    }));
  }, [findGeomSceneObjectForGizmo]);

  const resolveSelectedTargetForScaleGizmo = React.useCallback(() => {
    return resolveScaleTargetFromSelection({
      selection: useSelectionStore.getState(),
      bodies: getSceneState()?.bodies || EMPTY_OBJECT,
      findGeomSceneObjectForGizmo,
    });
  }, [findGeomSceneObjectForGizmo]);

  return {
    resolveSelectedBodyUuidForGizmo,
    resolveSelectedTargetForPositionGizmo,
    resolveSelectedTargetForRotationGizmo,
    resolveSelectedTargetForScaleGizmo,
    resolveSelectedVisualMeshGeomTargetsForGizmo,
  };
}
